import { useChatCopy, useChatInterfaceLocale } from '@/lib/chat-i18n';
import type { SettingsChange, SettingsProposal, ProposalView } from '@/lib/account-settings';
import { actionCopy } from '@/components/assistant-ui/action-copy';
import { MODEL_CHOICE_COPY } from './model-choices.generated';

function languageName(tag: string) {
  try {
    const name = new Intl.DisplayNames([tag], { type: 'language' }).of(tag) ?? tag;
    return name.charAt(0).toLocaleUpperCase(tag) + name.slice(1);
  } catch {
    return tag;
  }
}

/** Settings receipts use the same localized fields and statuses as their approval card. */
export function useSettingsChangeCopy() {
  const chrome = useChatCopy();
  const copy = actionCopy(useChatInterfaceLocale());
  const language = (value: string | null) =>
    value ? languageName(value) : chrome.automaticBrowserLanguage;
  return {
    approve: copy.approve,
    reject: copy.reject,
    error: chrome.settingsSaveFailed,
    change(change: SettingsChange) {
      const values: Record<string, string> = {
        theme: {
          light: chrome.themeLight,
          dark: chrome.themeDark,
          system: chrome.themeSystem,
        }[change.settings.theme ?? 'system'],
        language: language(change.settings.language),
        timezone: change.settings.timezone ?? chrome.automaticTimezone,
        chat_model_choice: MODEL_CHOICE_COPY[change.settings.chat_model_choice].name,
        personality: change.settings.personality ?? '',
        voice_prefs: Object.keys(change.previous.voice_prefs ?? {})
          .map((name) => {
            const value =
              change.settings.voice_prefs?.[
                name as keyof NonNullable<typeof change.settings.voice_prefs>
              ];
            return `${copy.fields[name] ?? name}: ${typeof value === 'object' ? JSON.stringify(value) : String(value)}`;
          })
          .join(', '),
      };
      return change.changed.map((key) => `${copy.fields[key]}: ${values[key]}`).join(' · ');
    },
    proposal(proposal: SettingsProposal, view: ProposalView) {
      const status =
        view === 'applied' ? copy.approved : view === 'dismissed' ? copy.rejected : copy.pending;
      return `${copy.fields.language}: ${language(proposal.language)} · ${status}`;
    },
  };
}
